/**
 * The durable side of the offline queue.
 *
 * IndexedDB rather than `localStorage`: a queued write has to survive the tab
 * being killed by the OS, which is the *normal* way a phone browser ends a
 * session, and `localStorage` is synchronous — blocking the main thread on
 * every log entry to serialise a growing array is exactly the stutter that
 * makes an app feel broken on the device this is for.
 *
 * Written against the raw IndexedDB API. The queue holds one object store with
 * four operations; a wrapper library would be more code to audit than the code
 * it replaces.
 */

const DB_NAME = 'nutrigeist-offline';
const DB_VERSION = 1;
const STORE = 'pending-writes';

/** What a queued write is allowed to be. */
export type PendingKind = 'diary' | 'water' | 'weight' | 'activity';

export interface PendingWrite {
  /** Also the `clientRequestId` sent to the server; see the shared schema. */
  id: string;
  kind: PendingKind;
  path: string;
  body: unknown;
  createdAt: number;
  attempts: number;
  /** Why the last attempt failed, for the user to see. */
  lastError: string | null;
  /** A short human label, so the pending list is readable rather than JSON. */
  label: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        // Replay must be in the order the user made the entries, not in
        // whatever order the keys happen to sort.
        store.createIndex('createdAt', 'createdAt');
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB unavailable'));
  });

  return dbPromise;
}

function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = work(transaction.objectStore(STORE));

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
      }),
  );
}

export function putPending(entry: PendingWrite): Promise<unknown> {
  return run('readwrite', (store) => store.put(entry));
}

export function deletePending(id: string): Promise<unknown> {
  return run('readwrite', (store) => store.delete(id));
}

/** Oldest first — the order they were made is the order they are sent. */
export async function listPending(): Promise<PendingWrite[]> {
  const all = await run<PendingWrite[]>('readonly', (store) => store.getAll());
  return all.sort((a, b) => a.createdAt - b.createdAt);
}

export function clearPending(): Promise<unknown> {
  return run('readwrite', (store) => store.clear());
}

/**
 * Whether this browser can back the queue at all.
 *
 * Private-mode Firefox and a handful of embedded webviews expose
 * `indexedDB` and then throw on open. Checking up front lets the app decline
 * the feature honestly instead of silently losing writes.
 */
export async function isQueueSupported(): Promise<boolean> {
  if (typeof indexedDB === 'undefined') return false;
  try {
    await openDb();
    return true;
  } catch {
    return false;
  }
}
